import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { navItems, siteConfig } from "@/config/site";

export default function SiteHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);

  const isHome = location.pathname === "/";
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.matchMedia("(max-width: 767px)").matches : false,
  );

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const media = window.matchMedia("(max-width: 767px)");
    const handleChange = () => setIsMobile(media.matches);
    handleChange();
    media.addEventListener("change", handleChange);
    return () => media.removeEventListener("change", handleChange);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname, location.search]);

  const transparent = isHome && !isMobile && !scrolled && !menuOpen;

  const activeClass = (to: string) => {
    const path = location.pathname;
    if (to === "/") return path === "/";
    if (to === "/search") return path === "/search";
    if (to === "/about") return path === "/about";
    return path.startsWith(to);
  };

  const submitSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    navigate(value ? `/search?q=${encodeURIComponent(value)}` : "/search");
    setSearchOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        transparent ? "bg-primary-950/25" : "bg-background-50/95 border-b border-background-200 backdrop-blur"
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="flex h-16 md:h-[72px] items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3 cursor-pointer shrink-0">
            <span
              className={`flex h-9 w-9 md:h-10 md:w-10 items-center justify-center rounded-lg ${
                transparent ? "bg-secondary-500 text-primary-950" : "bg-primary-900 text-secondary-500"
              }`}
            >
              <i className="ri-sun-foggy-line text-xl" aria-hidden="true"></i>
            </span>
            <span className="flex flex-col leading-tight">
              <span
                className={`font-heading text-lg md:text-xl font-bold tracking-wide ${
                  transparent ? "text-background-50" : "text-foreground-950"
                }`}
              >
                {siteConfig.name}
              </span>
              <span
                className={`text-[10px] tracking-[0.22em] ${
                  transparent ? "text-background-200/80" : "text-foreground-500"
                }`}
              >
                {siteConfig.englishName}
              </span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1" aria-label="主导航">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`rounded-md px-4 py-2 text-sm whitespace-nowrap transition-colors cursor-pointer ${
                  activeClass(item.to)
                    ? transparent
                      ? "bg-background-50/15 text-secondary-400 font-medium"
                      : "bg-primary-100 text-primary-800 font-medium"
                    : transparent
                      ? "text-background-100 hover:bg-background-50/10 hover:text-background-50"
                      : "text-foreground-700 hover:bg-background-200/70 hover:text-foreground-950"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <form
              onSubmit={submitSearch}
              className={`flex items-center gap-2 rounded-full border px-3 py-1.5 ${
                transparent ? "border-background-200/40 bg-background-50/10" : "border-background-300 bg-background-100"
              }`}
            >
              <span
                className={`flex h-5 w-5 items-center justify-center ${
                  transparent ? "text-background-200" : "text-foreground-500"
                }`}
              >
                <i className="ri-search-line text-sm" aria-hidden="true"></i>
              </span>
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索影视作品"
                aria-label="搜索影视作品"
                className={`w-36 bg-transparent text-sm outline-none ${
                  transparent
                    ? "text-background-50 placeholder:text-background-200/70"
                    : "text-foreground-900 placeholder:text-foreground-400"
                }`}
              />
              <button
                type="submit"
                className="rounded-full bg-secondary-500 px-3 py-1 text-xs font-medium text-primary-950 whitespace-nowrap transition-colors cursor-pointer hover:bg-secondary-400"
              >
                搜索
              </button>
            </form>
          </div>

          <div className="flex md:hidden items-center gap-2">
            <button
              type="button"
              onClick={() => setSearchOpen((prev) => !prev)}
              aria-label="搜索"
              className={`flex h-9 w-9 items-center justify-center rounded-md cursor-pointer ${
                transparent ? "text-background-50" : "text-foreground-800"
              }`}
            >
              <i className="ri-search-line text-lg" aria-hidden="true"></i>
            </button>
            <button
              type="button"
              onClick={() => setMenuOpen((prev) => !prev)}
              aria-label="打开菜单"
              aria-expanded={menuOpen}
              className={`flex h-9 w-9 items-center justify-center rounded-md cursor-pointer ${
                transparent ? "text-background-50" : "text-foreground-800"
              }`}
            >
              <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} aria-hidden="true"></i>
            </button>
          </div>
        </div>

        {searchOpen && (
          <form onSubmit={submitSearch} className="flex md:hidden items-center gap-2 pb-3">
            <input
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="输入片名、导演或演员"
              aria-label="搜索影视作品"
              className="h-10 flex-1 rounded-md border border-background-300 bg-background-100 px-3 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400"
            />
            <button
              type="submit"
              className="h-10 rounded-md bg-primary-800 px-4 text-sm font-medium text-background-50 whitespace-nowrap cursor-pointer"
            >
              搜索
            </button>
          </form>
        )}
      </div>

      {menuOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50 animate-fade-in">
          <nav className="flex flex-col px-4 py-3" aria-label="移动端导航">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`flex items-center justify-between rounded-md px-3 py-3 text-sm cursor-pointer ${
                  activeClass(item.to)
                    ? "bg-primary-100 text-primary-800 font-medium"
                    : "text-foreground-700 hover:bg-background-100"
                }`}
              >
                {item.label}
                <i className="ri-arrow-right-s-line text-foreground-400" aria-hidden="true"></i>
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}